import type { CSSProperties, ReactNode } from 'react';
import { useMemo } from 'react';

const TRANSIT_ICONS: readonly ReactNode[] = [
  <svg
    key="bus"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="3" width="16" height="16" rx="4" />
    <path d="M4 11h16" />
    <rect x="7" y="6" width="4" height="3" rx="1" />
    <rect x="13" y="6" width="4" height="3" rx="1" />
    <circle cx="8" cy="15.5" r="1.5" />
    <circle cx="16" cy="15.5" r="1.5" />
    <path d="M6 19v2M18 19v2" />
  </svg>,
  <svg
    key="train"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="2" width="14" height="17" rx="4" />
    <path d="M5 11h14" />
    <circle cx="9" cy="15" r="1" />
    <circle cx="15" cy="15" r="1" />
    <path d="M9 22l-2-3M15 22l2-3" />
    <path d="M9 6h6" />
  </svg>,
  <svg
    key="bike"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="5.5" cy="17" r="3.5" />
    <circle cx="18.5" cy="17" r="3.5" />
    <path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" fill="currentColor" />
    <path d="M12 17l2-7h3l2 7M5.5 17L8 7h3" />
    <path d="M8 7l4 5h4" />
  </svg>,
  <svg
    key="car"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 17h14v-5l-2-5H7L5 12v5z" />
    <circle cx="8" cy="17" r="2" />
    <circle cx="16" cy="17" r="2" />
    <path d="M10 17h4" />
    <path d="M3 12h2M19 12h2" />
  </svg>,
  <svg
    key="subway"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="6" y="3" width="12" height="15" rx="3" />
    <path d="M6 12h12" />
    <circle cx="9" cy="15" r="1" />
    <circle cx="15" cy="15" r="1" />
    <path d="M10 22l-2-4M14 22l2-4" />
    <path d="M10 7h4" />
  </svg>,
  <svg
    key="pin"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>,
];

interface FloatingIconItem {
  id: number;
  icon: ReactNode;
  size: number;
  top: number;
  duration: number;
  delay: number;
  opacity: number;
  drift: number;
}

interface FloatingIconCSS extends CSSProperties {
  '--drift'?: string;
}

const ICON_COUNT = 18;

export function FloatingIcons(): ReactNode {
  const items = useMemo<FloatingIconItem[]>(() => {
    return Array.from({ length: ICON_COUNT }, (_, i) => ({
      id: i,
      icon: TRANSIT_ICONS[i % TRANSIT_ICONS.length],
      size: 18 + Math.random() * 16,
      top: (i / ICON_COUNT) * 90 + Math.random() * 8,
      duration: 20 + Math.random() * 25,
      delay: -(Math.random() * 30),
      opacity: 0.12 + Math.random() * 0.14,
      drift: Math.random() * 30 - 15,
    }));
  }, []);

  return (
    <div className="floating-icons" aria-hidden="true">
      {items.map((item) => {
        const style: FloatingIconCSS = {
          width: item.size,
          height: item.size,
          top: `${item.top}%`,
          '--drift': `${item.drift}px`,
          animationDuration: `${item.duration}s`,
          animationDelay: `${item.delay}s`,
          opacity: item.opacity,
        };
        return (
          <div key={item.id} className="floating-icon" style={style}>
            {item.icon}
          </div>
        );
      })}
    </div>
  );
}
